<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🖥️ Apa itu Customer Display System (CDS)?',
    icon: 'ri-tv-2-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        <strong>Customer Display System</strong> adalah layar kedua yang menghadap ke
        pelanggan (mis. tablet/HP bekas ditaruh menghadap meja kasir), menampilkan rincian
        belanja secara <strong>real-time</strong> mengikuti apa yang sedang diinput kasir —
        item, subtotal, diskon, sampai kembalian setelah pembayaran tunai.
      </p>
      <p class="mb-0">
        Tujuannya supaya pelanggan bisa ikut memantau transaksinya sendiri (transparansi),
        tanpa perlu berbalik melihat layar kasir.
      </p>
    `,
  },
  {
    title: '💎 Fitur Eksklusif Paket Premium',
    icon: 'ri-vip-crown-2-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        CDS hanya tersedia untuk tenant dengan <strong>paket Premium</strong>. Kalau toko
        Anda masih di paket Starter/Pro, tombol untuk fitur ini <strong>tidak akan tampil
        sama sekali</strong> di halaman POS Kasir.
      </p>
      <VAlert type="warning" variant="tonal">
        ⚠️ Kalau Anda mencoba mengakses fiturnya lewat cara lain di luar tombol yang
        disediakan, sistem akan menolak dengan pesan: <em>"Fitur Customer Display System
        tidak tersedia di paket Anda saat ini. Silakan upgrade paket untuk
        mengaktifkannya."</em>
      </VAlert>
    `,
  },
  {
    title: '🔗 Cara Pairing Device Kedua',
    icon: 'ri-link',
    color: 'success',
    content: `
      <p class="mb-4">
        Pairing dilakukan <strong>1 kali per shift</strong> — device kedua tidak perlu
        login staf sama sekali, cukup ditukar dengan kode pairing.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Buka <strong>shift kasir</strong> Anda seperti biasa di halaman
          <strong>POS Kasir</strong></li>
        <li class="mb-2">Klik tombol <strong>"Hubungkan Layar Pelanggan"</strong> (ikon TV)
          di pojok kanan atas POS — muncul dialog berisi <strong>kode 6 digit</strong></li>
        <li class="mb-2">Di device kedua (browser terpisah), buka halaman pairing kosong
          — masukkan kode 6 digit tadi</li>
        <li class="mb-0">Begitu kode cocok, device kedua otomatis berpindah menjadi layar
          Customer Display, tersambung ke shift kasir yang sedang berjalan</li>
      </ol>
      <VAlert type="info" variant="tonal">
        💡 Kode kedaluwarsa? Klik <strong>"Buat Ulang"</strong> di dialog yang sama untuk
        mendapat kode 6 digit baru — tidak perlu membuka ulang shift.
      </VAlert>
    `,
  },
  {
    title: '⏱️ Kode Berlaku 5 Menit & Terputus Saat Shift Ditutup',
    icon: 'ri-time-line',
    color: 'error',
    content: `
      <p class="mb-4">
        Dua hal penting soal masa berlaku koneksi ini:
      </p>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Kondisi</th><th>Yang Terjadi</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Kode pairing 6 digit</strong></td>
            <td>Hanya berlaku <strong>5 menit</strong> sejak dibuat — kalau device kedua
              belum sempat memasukkannya dalam waktu itu, kode dianggap kedaluwarsa dan
              harus dibuat ulang</td>
          </tr>
          <tr>
            <td><strong>Koneksi yang sudah tersambung</strong></td>
            <td>Otomatis terputus begitu <strong>shift kasir ditutup</strong> — layar
              pelanggan perlu <strong>pairing ulang</strong> setiap kali kasir membuka
              shift baru</td>
          </tr>
        </tbody>
      </table>
      <VAlert type="warning" variant="tonal">
        ⚠️ Ini bukan bug — desainnya memang begitu, supaya koneksi layar pelanggan selalu
        terikat ke shift kasir yang sedang aktif, tidak "menempel" ke shift lama yang sudah
        ditutup.
      </VAlert>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        class="mb-2"
      >
        Kembali ke Daftar Modul
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar
        color="primary"
        variant="tonal"
        size="64"
        rounded="lg"
        class="me-4"
      >
        <VIcon
          icon="ri-tv-2-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Customer Display System
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Layar kedua menghadap pelanggan — rincian belanja & kembalian real-time
        </p>
      </div>
    </div>

    <VAlert
      type="info"
      variant="tonal"
      class="mb-6"
      icon="ri-information-line"
    >
      Fitur eksklusif <strong>paket Premium</strong>. Toko dengan paket Starter/Pro tidak
      akan melihat tombol fitur ini di POS Kasir.
    </VAlert>

    <VExpansionPanels
      variant="accordion"
      multiple
    >
      <VExpansionPanel
        v-for="(section, index) in sections"
        :key="index"
      >
        <VExpansionPanelTitle>
          <VIcon
            :icon="section.icon"
            :color="section.color"
            class="me-3"
          />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      ❓ Pertanyaan yang Sering Ditanyakan
    </h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Kenapa tombol "Hubungkan Layar Pelanggan" tidak muncul di POS saya?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Kemungkinan besar toko Anda belum berlangganan paket Premium — CDS eksklusif
          paket Premium. Hubungi admin PintuKasir untuk upgrade paket.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Device apa saja yang bisa dipakai sebagai layar pelanggan?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Perangkat apa pun yang punya browser — tablet, HP bekas, laptop kedua, dsb.
          Tidak perlu aplikasi khusus, cukup buka halaman pairing lewat browser.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah layar pelanggan perlu login akun staf?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak. Layar pelanggan tidak pernah login sebagai staf — satu-satunya
          "kredensial" yang dipakai adalah kode pairing 6 digit yang dibuat kasir.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Shift kasir tutup lalu buka lagi — apa layar pelanggan otomatis tersambung ulang?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak otomatis. Koneksi selalu terikat ke satu shift — begitu shift ditutup,
          koneksinya ikut terputus. Kasir perlu klik "Hubungkan Layar Pelanggan" lagi dan
          memasukkan kode baru di layar pelanggan setiap membuka shift baru.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      🔗 Halaman Terkait
    </h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Refund Berotorisasi', sub: 'Kembalikan uang pelanggan untuk transaksi selesai', icon: 'ri-refund-2-line', color: 'error', to: { name: 'userguide-kasir-lanjutan-refund' } },
          { title: 'POS Kasir', sub: 'Panduan lengkap kasir dan transaksi', icon: 'ri-shopping-cart-2-line', color: 'primary', to: { name: 'userguide-operasional-pos-kasir' } },
          { title: 'Sistem Antrian', sub: 'Mode Papan Antrian di layar pelanggan — beda dari Mode Transaksi di atas', icon: 'ri-hashtag', color: 'success', to: { name: 'userguide-operasional-sistem-antrian' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink
          :to="link.to"
          class="text-decoration-none"
        >
          <VCard
            variant="outlined"
            hover
          >
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar
                :color="link.color"
                variant="tonal"
                size="40"
                rounded
              >
                <VIcon
                  :icon="link.icon"
                  size="22"
                />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">
                  {{ link.title }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ link.sub }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
